import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {IReelStoppedData} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {ReelStopPresentationController} from "@nolimitcity/slot-game/bin/core/reelstoppresentation/ReelStopPresentationController";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {TweenLite} from "gsap";
import {TombOfNefertitiGameShaker} from "../gameshake/TombOfNefertitiGameShaker";
import {TombOfNefertitiParsedGameData} from "../server/data/TombOfNefertitiParsedGameData";
import {TombOfNefertitiGameConfig} from "../TombOfNefertitiGameConfig";
import {TombOfNefertitiSoundConfig} from "../TombOfNefertitiSoundConfig";

export class TombOfNefertitiReelStopPresentationController extends ReelStopPresentationController {

    public static readonly ADD_SCATTER_ATTENTION:string = "addReelAttention";
    public static readonly CLEAR_ATTENTION_FROM_REEL:string = "clearAttentionFromReel";
    public static readonly CLEAR_LANDING_ANIMATION_FROM_REEL:string = "clearAllLandingAnimationFromReel";
    public static readonly ADD_SCATTER_LANDING_ANIMATION:string = "scatterLandingAnimation";

    private INDEX_BEFORE_ATTENTION:number;
    private INDEXES_FOR_ATTENTION:number[];
    private gameMode:string;

    private _stickySymbols:boolean[] = [];
    private _indexes:number[] = [];
    private _isReelStopSoundPlayedOnQuickStop:boolean = false;
    private _fastSpinStatusOnSpinStart:boolean = false;

    public onReelBounce(data:IReelStoppedData):void {
        let reel = Reels.getReel(data.reelId)!,
            sym = Reels.getSymbol(data.reelId, 0)!,
            isStopSoundPlayed:boolean = false;

        if(!data.quickStop && !this._fastSpinStatusOnSpinStart) {
            if((sym.symName !== "W") && (sym.symName !== "S")) {
                isStopSoundPlayed = this.applyReelStopSound(data.reelId);
            }
        } else if(!this._isReelStopSoundPlayedOnQuickStop || !this._fastSpinStatusOnSpinStart) {
            SlotGame.sound.playEffect(TombOfNefertitiSoundConfig.instance.REEL_LANDING + (Math.floor(data.reelId / 3 + 1)));
            this._isReelStopSoundPlayedOnQuickStop = true;
            this._fastSpinStatusOnSpinStart = true;
        }

        if((data.reelId > this.INDEX_BEFORE_ATTENTION) && (!isStopSoundPlayed) && (sym.symName !== "W") && (sym.symName !== "S")) {
            if(!this._isReelStopSoundPlayedOnQuickStop || !this._fastSpinStatusOnSpinStart) {
                SlotGame.sound.playEffect(TombOfNefertitiSoundConfig.instance.REEL_LANDING + (Math.floor(data.reelId / 3 + 1)));
            }
        }

        if(sym.symName === "W") {
            if(!(this._gameData! as TombOfNefertitiParsedGameData).isWildRaidFeatureTriggered) {
                SlotGame.sound.playEffect(TombOfNefertitiSoundConfig.instance.WILD_LANDING);

                EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiGameShaker.START_GAME_SHAKING_REEL_AREA));
                TweenLite.to(() => {}, 0.3, {
                    onComplete : () => {
                        EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiGameShaker.STOP_GAME_SHAKING));
                    }
                });

                sym.changeState({
                    state : TombOfNefertitiGameConfig.instance.SYMBOL_STATES.attention,
                    onCompleteCallback : () => {
                        sym.changeState({
                            state : TombOfNefertitiGameConfig.instance.SYMBOL_STATES.normal
                        });
                    }
                });
            }
        }

        if(TombOfNefertitiGameConfig.instance.OVERLAPPING_SYMBOLS.indexOf((reel.view.children[3] as StateReelSymbol).symName) !== -1) {
            const offset = -30;

            reel.allSymbols[0]!.visible = false;

            (<PIXI.Container>reel.view.mask!).height = TombOfNefertitiGameConfig.instance.SYMBOL_HEIGHT_FOR_OVERLAPPING_SYMBOLS;
            (<PIXI.Container>reel.view.mask!).y = offset;
        }

        if(reel.allSymbols[1]!.symName === 'S') {
            EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiReelStopPresentationController.ADD_SCATTER_LANDING_ANIMATION, data.reelId));
        }

    }

    public onReelSpinStopped(data:IReelStoppedData):void {
        this.dispatchCompleteEvent(data.reelId);

        EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiReelStopPresentationController.CLEAR_ATTENTION_FROM_REEL, data.reelId));

        if(!(data.quickStop)) {
            if(!isNaN(this.INDEX_BEFORE_ATTENTION) && (this.INDEX_BEFORE_ATTENTION === data.reelId)) {
                EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiReelStopPresentationController.ADD_SCATTER_ATTENTION, this.INDEXES_FOR_ATTENTION));
            }
        } else {
            EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiReelStopPresentationController.CLEAR_ATTENTION_FROM_REEL, -1));
        }
    }

    protected parseFeatureGameData(data:TombOfNefertitiParsedGameData):void {
        let gameData:TombOfNefertitiParsedGameData = (data as TombOfNefertitiParsedGameData);
        this.INDEX_BEFORE_ATTENTION = gameData.indexBeforeAttention;
        this.INDEXES_FOR_ATTENTION = gameData.indexesForAttention;
        this.updateIndexesForAttention(data.wildRaidPositions);
        this.gameMode = gameData.mode;

        if(this.gameMode.indexOf("RESPIN") === -1) {
            this._indexes = [2, 5, 8, 11, 14];
        }

        this._stickySymbols = data.respinPositions;
    }

    protected resetFeature():void {
        this._indexes = [-1, -1, -1, -1, -1];

        // need to type cast it as this reduce will not work directly on boolean.
        this._stickySymbols = this._stickySymbols ? this._stickySymbols.reduce((acc, currEl) => acc.concat(<any>currEl), []) : [];

        for(let i:number = 0; i < this._stickySymbols.length; ++i) {
            if(this._stickySymbols[i]) {
                this._indexes[Math.floor(i / 3)] = i;
            }
        }

        this._isReelStopSoundPlayedOnQuickStop = false;
    }

    protected addFeatureEventHandlers():void {
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event:GameEvent) => this._stickySymbols = event.params[0].respinPositions);
        EventHandler.addEventListener(this, SpinEvent.START, (event:GameEvent) => this._fastSpinStatusOnSpinStart = event.params[0].isFastSpin);
    }

    private applyReelStopSound(reelId:number):boolean {
        if(this._indexes.indexOf(reelId) !== -1) {
            SlotGame.sound.playEffect(TombOfNefertitiSoundConfig.instance.REEL_LANDING + (Math.floor(reelId / 3 + 1)));
            return true;
        }
        return false;
    }

    private updateIndexesForAttention(wildRaidPositions:number[]):void {
        for(let i:number = 0; i < wildRaidPositions.length; i++) {
            let currentWildIndex = this.INDEXES_FOR_ATTENTION.indexOf((wildRaidPositions[i] + 2));
            if(currentWildIndex !== -1) {
                this.INDEXES_FOR_ATTENTION.splice(currentWildIndex, 1);
            }
        }
    }
}